import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Star, Instagram, Check} from 'lucide-react';
import {useAuth} from '../contexts/AuthContext';
import {doc, getDoc, updateDoc} from 'firebase/firestore';
import {db} from '../firebase';
import {
  getInstagramAccessToken,
  initiateInstagramAuth,
} from '../services/instagramService';
import {
  initiateYoutubeAuth,
  getYoutubeAccessToken,
} from '../services/youtubeService';
import { getTikTokAccessToken, initiateTikTokAuth } from '../services/tiktokService';

interface YoutubeVerification {
  channelId: string;
  subscriberCount: number;
  verified: boolean;
  verifiedAt: Date;
  lastChecked: Date;
}

interface InstagramVerification {
  username: string;
  followersCount: number;
  verified: boolean;
  verifiedAt: Date;
  lastChecked: Date;
}

interface TikTokVerification {
  username: string;
  followersCount: number;
  verified: boolean;
  verifiedAt: Date;
  lastChecked: Date;
}

const YoutubeIconSVGPath = "M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z";

const TikTokIconSVGPath = "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z";

const STAR_THRESHOLD = 10000;
const STAR_THRESHOLD_LABEL = STAR_THRESHOLD.toLocaleString();

const BecomeStarScreen: React.FC = () => {
  const navigate = useNavigate();
  const {user} = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [instagramData, setInstagramData] =
    useState<InstagramVerification | null>(null);
  const [youtubeData, setYoutubeData] = useState<YoutubeVerification | null>(
    null,
  );
  const [tiktokData, setTiktokData] = useState<TikTokVerification | null>(null);

  const updateStarStatus = async (userId: string, verificationData: any) => {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    // Check if any platform meets verification criteria
    const isInstagramVerified = userData?.instagramVerification?.followersCount >= STAR_THRESHOLD;
    const isYoutubeVerified = userData?.youtubeVerification?.subscriberCount >= STAR_THRESHOLD;
    const isTikTokVerified = userData?.tiktokVerification?.followersCount >= STAR_THRESHOLD;
    
    // Update isStarAccount if any platform is verified
    const isStarAccount = isInstagramVerified || isYoutubeVerified || isTikTokVerified;
    
    await updateDoc(doc(db, 'users', userId), {
      isStarAccount
    });
  };

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      if (!user) return;
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const data = userDoc.data();
      if (data?.instagramVerification) {
        setInstagramData(data.instagramVerification);
      }
    };
    fetchVerificationStatus();
  }, [user]);

  const handleInstagramAuth = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    try {
      const popup = initiateInstagramAuth();
      if (!popup) {
        throw new Error('Failed to open Instagram authentication window');
      }

      const messageHandler = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'instagram-auth') {
          try {
            // Backend returns complete user data after token exchange
            const userData = await getInstagramAccessToken(event.data.code);

            console.log('Instagram data:', userData);

            // Auto-verify based on follower count threshold
            const verificationData: InstagramVerification = {
              username: userData.username,
              followersCount: userData.followersCount || 0,
              verified: userData.followersCount >= STAR_THRESHOLD,
              verifiedAt: new Date(),
              lastChecked: new Date(),
            };

            await updateDoc(doc(db, 'users', user.uid), {
              instagramVerification: verificationData,
            });

            await updateStarStatus(user.uid, verificationData);
            setInstagramData(verificationData);
          } catch (err) {
            setError('Failed to verify Instagram account');
            console.error('Instagram verification error:', err);
          } finally {
            setLoading(false);
            window.removeEventListener('message', messageHandler);
            popup.close();
          }
        }
      };

      window.addEventListener('message', messageHandler);

      // Add cleanup if popup is closed manually
      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
            window.removeEventListener('message', messageHandler);
            setLoading(false);
          }
        } catch (err) {
          // Ignore cross-origin errors when checking popup.closed
        }
      }, 1000);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleYoutubeAuth = () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    try {
      const popup = initiateYoutubeAuth();
      if (!popup) {
        throw new Error('Failed to open YouTube authentication window');
      }

      const messageHandler = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'youtube-auth') {
          try {
            // Backend returns complete user data after token exchange
            const userData = await getYoutubeAccessToken(event.data.code);

            const verificationData: YoutubeVerification = {
              channelId: userData.channelId,
              subscriberCount: userData.subscriberCount,
              verified: userData.subscriberCount >= STAR_THRESHOLD,
              verifiedAt: new Date(),
              lastChecked: new Date(),
            };

            await updateDoc(doc(db, 'users', user.uid), {
              youtubeVerification: verificationData,
            });

            await updateStarStatus(user.uid, verificationData);
            setYoutubeData(verificationData);
          } catch (err) {
            setError('Failed to verify YouTube account');
            console.error('YouTube verification error:', err);
          } finally {
            setLoading(false);
            window.removeEventListener('message', messageHandler);
            popup.close();
          }
        }
      };

      window.addEventListener('message', messageHandler);

      // Add cleanup if popup is closed manually
      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
            window.removeEventListener('message', messageHandler);
            setLoading(false);
          }
        } catch (err) {
          // Ignore cross-origin errors when checking popup.closed
        }
      }, 1000);
    } catch (error) {
      setError('Failed to initiate YouTube verification');
      setLoading(false);
    }
  };

  const handleTikTokAuth = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    try {
      const popup = await initiateTikTokAuth();
      if (!popup) {
        throw new Error('Failed to open TikTok authentication window');
      }

      const messageHandler = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'tiktok-auth') {
          try {
            // Backend returns complete user data after token exchange
            const userData = await getTikTokAccessToken(event.data.code);

            const verificationData: TikTokVerification = {
              username: userData.username,
              followersCount: userData.followersCount,
              verified: userData.followersCount >= STAR_THRESHOLD,
              verifiedAt: new Date(),
              lastChecked: new Date(),
            };

            await updateDoc(doc(db, 'users', user.uid), {
              tiktokVerification: verificationData,
            });

            await updateStarStatus(user.uid, verificationData);
            setTiktokData(verificationData);
          } catch (err) {
            setError('Failed to verify TikTok account');
            console.error('TikTok verification error:', err);
          } finally {
            setLoading(false);
            window.removeEventListener('message', messageHandler);
            popup.close();
          }
        }
      };

      window.addEventListener('message', messageHandler);

      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
            window.removeEventListener('message', messageHandler);
            setLoading(false);
          }
        } catch (err) {
          // Ignore cross-origin errors when checking popup.closed
        }
      }, 1000);
    } catch (error) {
      setError('Failed to initiate TikTok verification');
      setLoading(false);
    }
  };

  const renderInstagramButton = () => {
    // Show verified if threshold is met
    if (instagramData && instagramData.verified) {
      return (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center">
          <div className="bg-green-500 rounded-full p-1 mr-3">
            <Check className="h-5 w-5 text-black" />
          </div>
          <span className="flex-1 text-green-700 font-medium">
            Verified ({instagramData.followersCount.toLocaleString()} followers)
          </span>
        </div>
      );
    }

    // Show not verified if threshold is not met
    if (instagramData && !instagramData.verified) {
      return (
        <button
          onClick={handleInstagramAuth}
          disabled={loading}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center gap-2 h-12 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-70"
        >
          {loading ? (
            <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
          ) : (
            <Instagram className="h-5 w-5" />
          )}
          {loading ? 'Connecting...' : `Reverify Instagram (${instagramData.followersCount.toLocaleString()} followers)`}
        </button>
      );
    }

    // Default: Connect button
    return (
      <button
        onClick={handleInstagramAuth}
        disabled={loading}
        className="w-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center gap-2 h-12 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-70"
      >
        {loading ? (
          <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
        ) : (
          <Instagram className="h-5 w-5" />
        )}
        {loading ? 'Connecting...' : 'Connect Instagram'}
      </button>
    );
  };

  const renderYoutubeButton = () => {
    if (youtubeData && youtubeData.subscriberCount >= STAR_THRESHOLD) {
      return (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center">
          <div className="bg-green-500 rounded-full p-1 mr-3">
            <Check className="h-5 w-5 text-black" />
          </div>
          <span className="flex-1 text-green-700 font-medium">
            Verified ({youtubeData.subscriberCount.toLocaleString()} subscribers)
          </span>
        </div>
      );
    }

    if (youtubeData && youtubeData.subscriberCount < STAR_THRESHOLD) {
      return (
        <button
          onClick={handleYoutubeAuth}
          disabled={loading}
          className="w-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center gap-2 h-12 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-70"
        >
          {loading ? (
            <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
          ) : (
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d={YoutubeIconSVGPath} />
            </svg>
          )}
          {loading ? 'Connecting...' : `Reverify YouTube (${youtubeData.subscriberCount.toLocaleString()} subscribers)`}
        </button>
      );
    }

    return (
      <button
        onClick={handleYoutubeAuth}
        disabled={loading}
        className="w-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center gap-2 h-12 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-70"
      >
        {loading ? (
          <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
        ) : (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d={YoutubeIconSVGPath} />
          </svg>
        )}
        {loading ? 'Connecting...' : 'Connect YouTube'}
      </button>
    );
  };

  const renderTikTokButton = () => {
    if (tiktokData && tiktokData.followersCount >= STAR_THRESHOLD) {
      return (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center">
          <div className="bg-green-500 rounded-full p-1 mr-3">
            <Check className="h-5 w-5 text-black" />
          </div>
          <span className="flex-1 text-green-700 font-medium">
            Verified ({tiktokData.followersCount.toLocaleString()} followers)
          </span>
        </div>
      );
    }
  
    if (tiktokData && tiktokData.followersCount < STAR_THRESHOLD) {
      return (
        <button
          onClick={handleTikTokAuth}
          disabled={loading}
          className="w-full bg-gray-800 hover:bg-gray-700 text-white flex items-center justify-center gap-2 h-12 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-70"
        >
          {loading ? (
            <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
          ) : (
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d={TikTokIconSVGPath} />
            </svg>
          )}
          {loading ? 'Connecting...' : `Reverify TikTok (${tiktokData.followersCount.toLocaleString()} followers)`}
        </button>
      );
    }
  
    return (
      <button
        onClick={handleTikTokAuth}
        disabled={loading}
        className="w-full bg-gray-800 hover:bg-gray-700 text-white flex items-center justify-center gap-2 h-12 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-70"
      >
        {loading ? (
          <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
        ) : (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d={TikTokIconSVGPath} />
          </svg>
        )}
        {loading ? 'Connecting...' : 'Connect TikTok'}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
        <div className="container mx-auto max-w-2xl py-8 sm:py-12 px-4 flex flex-col items-center">
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 bg-purple-500/10 rounded-full flex items-center justify-center">
              <svg className="h-10 w-10 text-purple-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900">Become a Star</h1>
          <p className="text-gray-400 max-w-md mx-auto">
            If you have {STAR_THRESHOLD_LABEL}+ followers or subscribers on Instagram, YouTube, or TikTok, you can become a verified star on Nexbii!
          </p>
        </div>

        <div className="w-full space-y-4">
          {renderInstagramButton()}
          {renderYoutubeButton()}
          {renderTikTokButton()}
        </div>

        {error && <p className="text-center text-sm text-red-500 mt-6">{error}</p>}

        <p className="text-gray-400 text-sm text-center mt-8 max-w-md">
          After connecting your account, we'll verify your follower count and
          upgrade your account to star status if eligible.
        </p>
      </div>
    </div>
  );
};

export default BecomeStarScreen;
